import forge from 'apis/forge';
import Modal from 'components/Modal';
import { Frame } from 'dragonmouse-shared/interfaces';
import { runExportWithToast } from 'components/Widget/ExportReadyToast';
import { useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { selectActiveScene, selectFrames } from 'redux/db/reducers';
import { blobToBase64, canExportSvg, packageFramesSvgAsset } from 'redux/db/util';
import { hideExportFramesAssetsModal } from 'redux/mode/actions';
import { RootReducer, selectSelectedFrames } from 'redux/reducers';
import store from 'redux/store';
import { buildExportFilename } from 'utils';
import * as Sentry from '@sentry/react';
import RadioCard from 'components/Widget/RadioCard';
import { ExportScope, resolveFrameIds, scopeText } from '../util';
import * as Shared from '../styles';

type Format = 'png' | 'svg';

interface Props {
	fixedFrameIds?: string[];
	onClose: () => void;
}

const FORMAT_CARDS: Array<{ format: Format; label: string; description: string }> = [
	{ format: 'png', label: 'PNG', description: 'Raster image of each frame' },
	{ format: 'svg', label: 'SVG', description: 'Vector image (Figma-compatible)' },
];

const FramesAssetsModal = ({ fixedFrameIds, onClose }: Props) => {
	const { selectedFrames, allFrames, activeScene } = useSelector(
		(r: RootReducer) => ({
			selectedFrames: selectSelectedFrames(r),
			allFrames: selectFrames(r.db),
			activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
		}),
		shallowEqual
	);

	const initialScope: ExportScope = selectedFrames.length > 0 ? 'selected' : 'all';
	const [scope] = useState<ExportScope>(initialScope);
	const [format, setFormat] = useState<Format>('png');
	const [submitted, setSubmitted] = useState(false);

	const activeFrames: Frame[] = useMemo(() => {
		const ids = resolveFrameIds(fixedFrameIds, scope, selectedFrames, allFrames);
		return ids.map((id) => allFrames.find((f) => f.id === id)).filter((f): f is Frame => !!f);
	}, [fixedFrameIds, scope, selectedFrames, allFrames]);

	const frameCount = activeFrames.length;
	const svgDisabled = format === 'svg' && !canExportSvg(activeFrames);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const closeModal = () => {
		store.dispatch(hideExportFramesAssetsModal());
		onClose();
	};

	const onDownload = async () => {
		if (frameCount === 0 || svgDisabled) return;
		setSubmitted(true);
		closeModal();
		const isSingle = frameCount === 1;
		const ext = isSingle ? format : 'zip';
		const token = isSingle ? format : `${format}s`;
		const filename = buildExportFilename(activeScene?.title, token, format, ext);
		const label = format.toUpperCase();
		const pendingLabel = isSingle ? `Preparing ${label}…` : `Packaging ${label}s…`;
		await runExportWithToast({
			pendingLabel,
			errorLabel: 'Export failed. Please try again.',
			run: async () => {
				if (format === 'svg') {
					const { blob, extension } = await packageFramesSvgAsset(activeFrames);
					const { exportId } = await forge.exportPrebuiltAsset({
						data: await blobToBase64(blob),
						filename,
						extension,
						label: 'Frame SVG',
					});
					return { exportId, filename };
				}
				const { exportId } = await forge.exportFramesAssets({
					frameIds: activeFrames.map((f) => f.id),
					format: 'png',
					filename,
				});
				return { exportId, filename };
			},
		}).catch((error) => Sentry.captureException(error));
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const downloadDisabled = submitted || frameCount === 0 || svgDisabled;

	const footerInfo = !fixedFrameIds ? (
		<div>{scopeText(selectedFrames.length, allFrames.length)}</div>
	) : undefined;

	return (
		<Modal
			title="Export Assets"
			onClose={closeModal}
			onCancel={closeModal}
			onSubmit={onDownload}
			submitLabel="Download"
			disabled={downloadDisabled}
			loading={false}
			footerInfo={footerInfo}
		>
			<Shared.Body>
				<Shared.RadioGrid>
					{FORMAT_CARDS.map((card) => (
						<RadioCard
							key={card.format}
							active={format === card.format}
							label={card.label}
							description={card.description}
							onClick={() => setFormat(card.format)}
						/>
					))}
				</Shared.RadioGrid>
			</Shared.Body>
		</Modal>
	);
};

export default FramesAssetsModal;
